import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { BodyText, HeaderText, toast } from "@/components/ui";
import { useUpdateResultSummary } from "@/features/Patient/MedicalRecords/Labs/network/useUpdateResultSummary";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { hasMarkdownFormatting } from "@/lib/utils";
import type { DiagnosticReport } from "@curai/js-fhir-tools";
import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ResultSummaryEditor } from "./ResultSummaryEditor";

export const ResultSummary = ({
  diagnosticReport,
  patientId,
}: {
  diagnosticReport?: DiagnosticReport;
  patientId: string;
}) => {
  const summaryText: string | undefined = diagnosticReport?.resultSummary?.text;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(summaryText);
  // Track whether we are currently in an edit session so the server-sync
  // effect does not overwrite an in-progress edit.
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  const updateResultSummary = useUpdateResultSummary({
    patientId,
    diagnosticReportId: diagnosticReport?.id ?? "",
  });

  useEffect(() => {
    if (!isEditingRef.current) {
      setValue(summaryText);
    }
  }, [summaryText]);

  if (!summaryText) {
    return null;
  }

  const handleSave = async (newText: string) => {
    if (!diagnosticReport?.id) return;
    try {
      await updateResultSummary.mutateAsync({ text: newText });
      setValue(newText);
      setIsEditing(false);
    } catch {
      toast.error("Failed to update lab comment");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 flex-shrink-0 stroke-ai" />
        <HeaderText size="sm" textColor="superclinical">
          AI generated Lab Comment (Suggested)
        </HeaderText>
      </div>
      {isEditing ? (
        <ResultSummaryEditor
          defaultValue={value}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={updateResultSummary.isPending}
        />
      ) : (
        <EditableRecordButton onClick={() => setIsEditing(true)}>
          {hasMarkdownFormatting(value ?? "") ? (
            <MarkdownRenderer content={value ?? ""} />
          ) : (
            <BodyText size="sm" preserveLineBreaks>
              {value}
            </BodyText>
          )}
        </EditableRecordButton>
      )}
    </div>
  );
};
